import React, { useState, useEffect } from "react";
import { Calendar, Clock, User, Mail, Phone, FileText } from "lucide-react";
const AppointmentForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    "appointment-date": "",
    "appointment-time": "",
    explain: "",
  });
  const [bookedSlots, setBookedSlots] = useState([]);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    if (formData["appointment-date"]) {
      checkAvailability(formData["appointment-date"]);
    }
  }, [formData["appointment-date"]]);

  const checkAvailability = async (date) => {
    try {
      const response = await fetch(
        `https://connectengagegrow.shop/ceg/Backend/check-slots.php?date=${date}`
      );
      const text = await response.text();
      const data = JSON.parse(text);
      if (data.success && Array.isArray(data.booked_slots)) {
        const formattedSlots = data.booked_slots.map((time) =>
          time.slice(0, 5)
        );
        setBookedSlots(formattedSlots);
      }
    } catch (error) {
      console.error("Error checking availability:", error);
    }
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push("Name is required");
    if (!formData.email.trim()) errors.push("Email is required");
    if (!/^\S+@\S+\.\S+$/.test(formData.email))
      errors.push("Invalid email format");
    if (!formData.phone.trim()) errors.push("Phone number is required");

    const phoneNumber = formData.phone.replace(/\D/g, ""); // Remove non-numeric characters

    if (phoneNumber.length < 10 || phoneNumber.length > 14)
      errors.push("Invalid phone number format");

    if (!formData["appointment-date"])
      errors.push("Appointment date is required");
    if (!formData["appointment-time"]) errors.push("Please select a time slot");
    if (!formData.explain.trim())
      errors.push("Please provide a reason for the appointment");

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage(validationErrors.join(", "));
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await fetch(
        "https://connectengagegrow.shop/ceg/Backend/book-appointment.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("Appointment booked successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          "appointment-date": "",
          "appointment-time": "",
          explain: "",
        });
        setSelectedTimeSlot(null);
        if (formData["appointment-date"]) {
          checkAvailability(formData["appointment-date"]);
        }
      } else {
        setMessage(
          data.message || "Failed to book appointment. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setMessage("Unable to book appointment. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Format phone number as (XXX) XXX-XXXX
      const formatted = value
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setMessage("");
  };

  const isTimeBooked = (time) => bookedSlots.includes(time);

  const selectTimeSlot = (time) => {
    if (!isTimeBooked(time)) {
      setSelectedTimeSlot(time);
      setFormData((prev) => ({ ...prev, "appointment-time": time }));
      setMessage("");
    }
  };

  const getTimeSlotStyle = (time) => {
    const isBooked = bookedSlots.includes(time);
    const isSelected = selectedTimeSlot === time;

    let baseStyle = `
      w-full p-2 rounded-md border text-sm font-medium 
      transition-all duration-200 relative
    `;

    if (isBooked) {
      return `${baseStyle} bg-red-100 border-red-300 text-red-600 cursor-not-allowed line-through text-lg font-bold`;
    } else if (isSelected) {
      return `${baseStyle} bg-green-100 border-green-500 text-green-700 shadow-sm`;
    } else {
      return `${baseStyle} bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300`;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f4f1] flex items-center justify-center p-4 ">
      <div className="max-w-3xl w-full bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#704735] mb-2">
            Schedule Your Appointment
          </h2>
          <p className="text-gray-500 text-sm">
            Please fill in your details below to book your appointment
          </p>
        </div>

        {message && (
          <div
            className={`p-4 mb-6 rounded-lg ${
              message.includes("success")
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <User className="w-4 h-4 mr-2" />
                Name <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#704735]"
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <Mail className="w-4 h-4 mr-2" />
                Email <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#704735]"
                placeholder="your.email@example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <Phone className="w-4 h-4 mr-2" />
                Phone <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#704735]"
                placeholder="(123) 456-7890"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <Calendar className="w-4 h-4 mr-2" />
                Date <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="date"
                name="appointment-date"
                value={formData["appointment-date"]}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#704735]"
              />
            </div>
          </div>

          {formData["appointment-date"] && (
            <div className="space-y-3">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <Clock className="w-4 h-4 mr-2" />
                Time Slot <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {timeSlots.map((time) => (
                  <div key={time} className="relative group">
                    <button
                      type="button"
                      onClick={() => selectTimeSlot(time)}
                      disabled={isTimeBooked(time)}
                      className={getTimeSlotStyle(time)}
                    >
                      {time}
                    </button>
                    {isTimeBooked(time) && (
                      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-red-100 text-red-600 text-xs rounded-lg shadow-lg whitespace-nowrap">
                        Not available
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-50 border border-red-200 rounded-sm mr-2"></div>
                  Booked
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-50 border border-green-500 rounded-sm mr-2"></div>
                  Selected
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-white border border-gray-200 rounded-sm mr-2"></div>
                  Available
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <FileText className="w-4 h-4 mr-2" />
              Reason for Appointment{" "}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="explain"
              value={formData.explain}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#704735]"
              placeholder="Please briefly describe the reason for your appointment"
            />
          </div>

          <button
            type="submit"
            disabled={
              isSubmitting ||
              !formData["appointment-time"] ||
              isTimeBooked(formData["appointment-time"])
            }
            className="w-full bg-[#704735] text-white font-medium py-3 px-4 rounded-md text-sm tracking-wide
              transition-all duration-200 hover:bg-[#5a3928] disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              "Book Appointment"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;
